// Copyright (c) 2023, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 Inc. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { contactDetails, details, routes, INTERNAL_EMAIL_DOMAIN_SUFFIX } from "../../config/constant";
import { useNavigate } from "react-router-dom";
import { RootState, useAppSelector } from "../../slices/store";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Link,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import { useState } from "react";
import { ExamState } from "../../utils/types";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";

export default function StartExamAlertDialog({ isResume }: { isResume: boolean }) {
  const exam = useAppSelector((state: RootState) => state.selectedExam);
  const contact = useAppSelector((state: RootState) => state.contact);
  const [open, setOpen] = useState(false);
  const [checkAgreement, setCheckAgreement] = useState({
    point1: false,
    point2: false,
  });
  let navigate = useNavigate();

  // Check if user is WSO2
  const email = contact?.response?.email || "";
  const isWso2User = email.endsWith(INTERNAL_EMAIL_DOMAIN_SUFFIX);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setCheckAgreement({
      point1: false,
      point2: false,
    });
    setOpen(false);
  };

  const startExam = () => {
    var examPaperType = exam.response[0]?.examPaperType;
    navigate(`${routes.exams}/${exam.response[0]?.examId}`);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckAgreement({
      ...checkAgreement,
      [event.target.name]: event.target.checked,
    });
  };

  return (
    <div>
      {isResume && isWso2User ? (
        <Typography variant="body2" ml={1}>
          {ExamState.ATTEMPTED}
        </Typography>
      ) : (
        <Tooltip title={isResume ? "Resume Exam" : "Start Exam"} arrow>
          <IconButton
            onClick={handleClickOpen}
            size="small"
            sx={{
              color: "#FF7300",
              width: 32,
              height: 32,
              "&:hover": {
                backgroundColor: "rgba(255, 115, 0, 0.04)",
              },
            }}
          >
            <PlayCircleFilledIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="lg"
      >
        <DialogTitle id="alert-dialog-title">
          {"You are about to start the exam!"}
        </DialogTitle>
        <DialogContent sx={{ ml: 1, mt: 1 }}>
          <DialogContentText id="alert-dialog-description">
            {details.startExamAlert}
          </DialogContentText>
        </DialogContent>
        <DialogContent sx={{ ml: 3, mt: -4 }}>
          <FormControl>
            <FormGroup>
              <FormControlLabel
                control={<Checkbox name="point1" onChange={handleChange} />}
                label={
                  <Typography variant="body1" align="left" component="p">
                    I have thoroughly read and comprehended the{" "}
                    <Link
                      underline="none"
                      color="secondary.main"
                      href={contactDetails.examinationGuidelinesUrl}
                      target="_blank"
                    >
                      examination guidelines
                    </Link>
                  </Typography>
                }
              />
              <FormControlLabel
                control={<Checkbox name="point2" onChange={handleChange} />}
                label={
                  "Both my webcam input and screen are currently being recorded."
                }
              />
            </FormGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button color="warning" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            disabled={!checkAgreement.point1 || !checkAgreement.point2}
            sx={{ paddingX: 2 }}
            variant="contained"
            onClick={startExam}
            autoFocus
          >
            Start Exam
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
